import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const features = [
  {
    title: 'Student roster & CSV import',
    description: 'Add students individually or bulk-upload a CSV. Organize them into placement batches by graduation year or drive.',
    icon: 'Users',
    color: 'emerald',
    points: ['One-by-one or CSV upload', 'Batch by year or drive', 'Roll numbers & grad year'],
  },
  {
    title: 'Credit pools',
    description: 'Allocate interview credits to a batch. Credits are reserved on assignment and released automatically if a session is cancelled.',
    icon: 'Wallet',
    color: 'sky',
    points: ['Per-batch allocation', 'Reserve on assign', 'Auto-release on cancel'],
  },
  {
    title: 'Reusable campaigns',
    description: 'Design an interview once — role, type, difficulty and topics — then assign it to hundreds of students in a few clicks.',
    icon: 'Send',
    color: 'indigo',
    points: ['Define once, reuse', 'Role & difficulty presets', 'Assign to a whole cohort'],
  },
  {
    title: 'Live completion tracking',
    description: 'See who has started, who has finished, and who needs a nudge — updated in real time as your batch works through interviews.',
    icon: 'Activity',
    color: 'amber',
    points: ['Started vs. completed', 'Per-student status', 'Real-time updates'],
  },
  {
    title: 'Performance analytics',
    description: 'Batch-level score distributions and per-student breakdowns, built on the same evaluation your students already receive.',
    icon: 'BarChart3',
    color: 'rose',
    points: ['Score distributions', 'Per-student detail', 'Exportable results'],
  },
  {
    title: 'Built on the prepxai engine',
    description: 'Students take the exact AI interviews, scoring and reports prepxai is known for. Nothing new for them to learn.',
    icon: 'Sparkles',
    color: 'emerald',
    points: ['Same AI interviews', 'Same scoring & reports', 'Zero student onboarding'],
  },
];

const colorClasses = {
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/20' },
  sky: { bg: 'bg-sky-500/10', text: 'text-sky-500', border: 'border-sky-500/20' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/20' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-500', border: 'border-indigo-500/20' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-500', border: 'border-rose-500/20' },
};

const FeatureCard = ({ feature, idx }) => {
  const colors = colorClasses[feature.color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: idx * 0.08 }}
      className="group flex h-full flex-col rounded-2xl border border-[var(--lp-line)] bg-white p-6 transition-colors duration-300 hover:border-[var(--lp-accent)]/40"
    >
      <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl border ${colors.bg} ${colors.border}`}>
        <Icon name={feature.icon} size={24} className={colors.text} />
      </div>

      <h3 className="mb-3 text-xl text-[var(--lp-ink)]">{feature.title}</h3>

      <p className="mb-6 flex-grow text-sm leading-relaxed text-[var(--lp-ink-soft)]">
        {feature.description}
      </p>

      <div className="mt-auto space-y-2 border-t border-[var(--lp-line)] pt-4">
        {feature.points.map((point) => (
          <div key={point} className="flex items-center gap-2 text-sm text-[var(--lp-ink)]">
            <Icon name="CheckCircle2" size={14} className={colors.text} />
            {point}
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const InstitutionFeaturesSection = () => {
  return (
    <section id="platform" className="relative z-10 border-t border-[var(--lp-line)] bg-white py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-[var(--lp-accent)]">
            The platform
          </p>
          <h2 className="mb-4 mt-4 text-3xl tracking-tight text-[var(--lp-ink)] md:text-4xl">
            Everything your placement cell needs, <span className="text-[var(--lp-accent)]">in one place.</span>
          </h2>
          <p className="text-lg leading-relaxed text-[var(--lp-ink-soft)]">
            Manage students, fund interviews, launch campaigns and track outcomes — all on top of the prepxai interview engine your students already use.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, idx) => (
            <FeatureCard key={feature.title} feature={feature} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default InstitutionFeaturesSection;
